// src/pages/ServiceClient.tsx
import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaComments, FaMicrophone, FaSearch, FaBolt, FaFire, FaStore, 
  FaCrown, FaUsers, FaPlusCircle, FaPassport, FaUserCircle, 
  FaPaperPlane, FaRobot, FaHeadset, FaStopCircle
} from 'react-icons/fa';

const ServiceClient: React.FC = () => {
  const [chatMessage, setChatMessage] = useState('');
  const [messages, setMessages] = useState([
    { role: 'ai', text: 'Bonjour ! Je suis l\'assistant Forfeo. Comment puis-je vous aider aujourd\'hui ?' }
  ]);
  const [isVoiceActive, setIsVoiceActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const recognitionRef = useRef<any>(null);
  const chatMessageRef = useRef('');
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Maintient la référence à jour pour l'envoi automatique vocal
  useEffect(() => {
    chatMessageRef.current = chatMessage;
  }, [chatMessage]);

  // ✅ CORRECTION : Bouton d'activation du micro avec création d'une NOUVELLE instance à chaque fois
  const toggleVoice = () => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      alert("Désolé, votre navigateur ne supporte pas la reconnaissance vocale (utilisez Chrome ou Safari à jour).");
      return;
    }

    // 🍏 ASTUCE iOS SAFARI : On initialise et on charge l'audio silencieusement au clic
    if (!audioRef.current) {
      audioRef.current = new Audio();
    }
    audioRef.current.load();

    if (isVoiceActive) {
      // Si on écoute déjà, on force l'arrêt
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      setIsVoiceActive(false);
    } else {
      // Couper la voix de Forfy si elle était en train de parler
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      // ✅ LE SECRET EST ICI : Créer une instance TOUTE NEUVE pour contourner le bug iOS
      const recognition = new SpeechRecognition();
      recognition.continuous = false; // S'arrête tout seul quand on finit de parler
      recognition.interimResults = true;
      recognition.lang = 'fr-CA';

      recognition.onstart = () => setIsVoiceActive(true);

      recognition.onresult = (event: any) => {
        const transcript = Array.from(event.results)
          .map((result: any) => result[0].transcript)
          .join('');
        setChatMessage(transcript);
        chatMessageRef.current = transcript; // Synchro ultra rapide
      };

      recognition.onend = () => {
        setIsVoiceActive(false);
        // Si l'utilisateur a parlé, on envoie le message
        if (chatMessageRef.current.trim()) {
          const finalMessage = chatMessageRef.current;
          chatMessageRef.current = ''; // On vide immédiatement pour le prochain tour
          setChatMessage('');
          handleSendMessage(undefined, finalMessage, true);
        }
      };

      recognitionRef.current = recognition;
      setChatMessage('');
      chatMessageRef.current = '';
      recognition.start();
    }
  };

  const handleSendMessage = async (e?: React.FormEvent, presetText?: string, wantAudio: boolean = false) => {
    e?.preventDefault();
    const textToSend = presetText || chatMessage;
    if (!textToSend.trim()) return;

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    setMessages(prev => [...prev, { role: 'user', text: textToSend }]);
    setChatMessage('');

    setMessages(prev => [...prev, { role: 'ai', text: wantAudio ? 'Forfy vous a entendu et réfléchit...' : 'Forfy réfléchit...' }]);

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message: textToSend, wantAudio }),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();

      setMessages(prev => {
        const newMessages = [...prev];
        newMessages[newMessages.length - 1] = { 
          role: 'ai', 
          text: data.reply || "Désolé, je n'ai pas pu formuler de réponse." 
        };
        return newMessages;
      });

      // 🔊 JOUER LA VOIX DE FORFY
      if (data.audioUrl) {
        if (!audioRef.current) {
           audioRef.current = new Audio();
        }
        audioRef.current.src = data.audioUrl;
        
        audioRef.current.play().catch(err => {
            console.error("Lecture audio bloquée par le navigateur:", err);
        });
      }

    } catch (error) {
      console.error("Erreur de communication avec l'IA :", error);
      setMessages(prev => {
        const newMessages = [...prev];
        newMessages[newMessages.length - 1] = { 
          role: 'ai', 
          text: "Désolé, nos serveurs sont actuellement surchargés. Veuillez réessayer dans quelques instants." 
        };
        return newMessages;
      });
    }
  };

  const quickQuestions = [
    "Comment réserver une expérience ?",
    "Où trouver les offres flash ?",
    "Comment fonctionne Forfeo Finds ?",
    "Comment inscrire mon entreprise ?"
  ];

  return (
    <div className="min-h-screen bg-slate-50 pb-20 font-sans">
      
      {/* A. HERO SECTION PREMIUM */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 text-white pt-24 pb-32 px-4 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
        <div className="container mx-auto max-w-5xl relative z-10 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="bg-blue-600/30 text-blue-200 py-1.5 px-4 rounded-full text-xs font-black uppercase tracking-widest mb-6 inline-block border border-blue-500/30">
              Assistance 24/7 • FR / EN
            </span>
            <h1 className="text-4xl md:text-6xl font-black mb-6 tracking-tight">
              Service Client <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300 italic font-light">Intelligent</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto mb-10">
              Notre centre d'assistance propulsé par l'IA et notre équipe humaine sont là pour sublimer votre expérience Forfeo.
            </p>
          </motion.div>
        </div>
      </section>

      {/* B & C. CHAT & VOIX (Grid Layout) */}
      <section className="container mx-auto max-w-6xl px-4 -mt-20 relative z-20">
        <div className="grid md:grid-cols-2 gap-6">
          
          {/* PANNEAU CHAT */}
          <div className="bg-white rounded-3xl shadow-xl border border-slate-100 flex flex-col h-[500px] overflow-hidden">
            <div className="bg-slate-900 p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                <FaRobot className="text-white text-lg" />
              </div>
              <div>
                <h3 className="text-white font-bold leading-tight">Assistant Chat</h3>
                <p className="text-blue-300 text-xs flex items-center gap-1"><span className="w-2 h-2 bg-green-400 rounded-full inline-block animate-pulse"></span> En ligne</p>
              </div>
            </div>

            <div className="flex-1 p-4 overflow-y-auto bg-slate-50 space-y-4">
              {messages.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] p-3 rounded-2xl text-sm ${
                    msg.role === 'user' ? 'bg-blue-600 text-white rounded-br-sm' : 'bg-white border border-slate-200 text-slate-700 rounded-bl-sm shadow-sm'
                  }`}>
                    {msg.text}
                  </div>
                </div>
              ))}
            </div>

            <div className="p-3 bg-white border-t border-slate-100">
              <div className="flex flex-wrap gap-2 mb-3">
                {quickQuestions.map((q, i) => (
                  <button key={i} onClick={() => handleSendMessage(undefined, q)} className="bg-slate-100 hover:bg-slate-200 text-slate-600 text-[10px] font-bold px-3 py-1.5 rounded-full transition">
                    {q}
                  </button>
                ))}
              </div>
              <form onSubmit={handleSendMessage} className="flex gap-2 relative">
                <input 
                  type="text" 
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                  placeholder="Posez votre question..." 
                  className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                />
                <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white w-12 rounded-xl flex items-center justify-center transition">
                  <FaPaperPlane />
                </button>
              </form>
            </div>
          </div>

          {/* PANNEAU VOCAL (NOUVEAU) */}
          <div className="bg-gradient-to-br from-indigo-900 to-slate-900 rounded-3xl shadow-xl border border-indigo-800 flex flex-col items-center justify-center p-8 text-center relative overflow-hidden h-[500px]">
            {isVoiceActive && (
              <div className="absolute inset-0 flex items-center justify-center opacity-20 pointer-events-none">
                <div className="w-64 h-64 bg-cyan-400 rounded-full blur-3xl animate-pulse"></div>
              </div>
            )}
            
            <div className="relative z-10">
              <div className={`w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center cursor-pointer transition-all duration-500 ${isVoiceActive ? 'bg-red-500 shadow-[0_0_40px_rgba(239,68,68,0.6)] scale-110' : 'bg-white/10 hover:bg-white/20'}`}
                   onClick={toggleVoice}>
                {isVoiceActive ? <FaStopCircle className="text-white text-4xl" /> : <FaMicrophone className="text-white text-4xl" />}
              </div>
              
              <h3 className="text-2xl font-black text-white mb-2">
                {isVoiceActive ? 'Je vous écoute...' : 'Assistance Vocale'}
              </h3>
              <p className="text-indigo-200 text-sm max-w-xs mx-auto mb-8">
                {isVoiceActive ? 'Parlez naturellement. L\'envoi se fera tout seul quand vous aurez terminé.' : 'Appuyez sur le micro et posez votre question à voix haute.'}
              </p>

              {!isVoiceActive && (
                <div className="bg-white/5 border border-white/10 p-4 rounded-2xl backdrop-blur-sm text-left">
                  <p className="text-xs text-green-400 font-bold uppercase tracking-widest mb-2 flex items-center gap-2">
                    <FaHeadset /> Forfy Vocal Actif
                  </p>
                  <p className="text-sm text-slate-300">
                    L'IA va automatiquement transcrire votre demande, chercher dans nos données, et vous répondre à voix haute !
                  </p>
                </div>
              )}
            </div>
          </div>

        </div>
      </section>

      {/* D. CENTRE D'AIDE INTELLIGENT */}
      <section className="container mx-auto max-w-5xl px-4 mt-20">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-black text-slate-900 mb-4">Centre d'Aide</h2>
          <div className="relative max-w-xl mx-auto">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input 
              type="text" 
              placeholder="Rechercher une aide (ex: Club Forfeo, Boutique...)" 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-white border border-slate-200 rounded-2xl pl-12 pr-4 py-4 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { icon: FaStore, title: "Réservations", desc: "Gérer vos achats" },
            { icon: FaCrown, title: "Club Forfeo", desc: "Avantages VIP" },
            { icon: FaFire, title: "Bons Plans", desc: "Finds & Last Call" },
            { icon: FaPlusCircle, title: "Partenaires", desc: "Espace entreprise" },
          ].map((cat, i) => (
            <div key={i} className="bg-white p-6 rounded-3xl border border-slate-100 hover:shadow-lg transition cursor-pointer text-center group">
              <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <cat.icon className="text-xl" />
              </div>
              <h4 className="font-bold text-slate-900 text-sm mb-1">{cat.title}</h4>
              <p className="text-[11px] text-slate-500 uppercase tracking-wider">{cat.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* E. ACTIONS RAPIDES */}
      <section className="container mx-auto max-w-5xl px-4 mt-20">
        <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest mb-6 px-2">Accès Rapides Forfeo</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 gap-4">
          <Link to="/boutique" className="flex items-center gap-3 bg-purple-50 hover:bg-purple-100 text-purple-900 p-4 rounded-2xl transition border border-purple-100">
            <FaStore className="text-purple-500" /> <span className="font-bold text-sm">Boutique</span>
          </Link>
          <Link to="/forfeo-finds" className="flex items-center gap-3 bg-orange-50 hover:bg-orange-100 text-orange-900 p-4 rounded-2xl transition border border-orange-100">
            <FaFire className="text-orange-500" /> <span className="font-bold text-sm">Forfeo Finds</span>
          </Link>
          <Link to="/last-call" className="flex items-center gap-3 bg-red-50 hover:bg-red-100 text-red-900 p-4 rounded-2xl transition border border-red-100">
            <FaBolt className="text-red-500" /> <span className="font-bold text-sm">Last Call</span>
          </Link>
          <Link to="/visiter-quebec" className="flex items-center gap-3 bg-indigo-50 hover:bg-indigo-100 text-indigo-900 p-4 rounded-2xl transition border border-indigo-100">
            <FaPassport className="text-indigo-500" /> <span className="font-bold text-sm">Visiter Québec</span>
          </Link>
          <Link to="/club" className="flex items-center gap-3 bg-yellow-50 hover:bg-yellow-100 text-yellow-900 p-4 rounded-2xl transition border border-yellow-200">
            <FaCrown className="text-yellow-600" /> <span className="font-bold text-sm">Club Forfeo</span>
          </Link>
          <Link to="/reseau-local" className="flex items-center gap-3 bg-blue-50 hover:bg-blue-100 text-blue-900 p-4 rounded-2xl transition border border-blue-100">
            <FaUsers className="text-blue-500" /> <span className="font-bold text-sm">Réseau Local</span>
          </Link>
          <Link to="/register-business" className="flex items-center gap-3 bg-green-50 hover:bg-green-100 text-green-900 p-4 rounded-2xl transition border border-green-100">
            <FaPlusCircle className="text-green-500" /> <span className="font-bold text-sm">Ajouter Entreprise</span>
          </Link>
          <Link to="/login" className="flex items-center gap-3 bg-slate-100 hover:bg-slate-200 text-slate-900 p-4 rounded-2xl transition border border-slate-200">
            <FaUserCircle className="text-slate-500" /> <span className="font-bold text-sm">Connexion</span>
          </Link>
        </div>
      </section>

    </div>
  );
};

export default ServiceClient;
